import { useEffect, useRef, useSyncExternalStore } from 'react';
import type { CoverageOption } from '../../../../../api/submissions/submission-coverages.types';
import { unquotedOptionsFor } from './quoteOptionsModel';

/**
 * A submission's cart is the options saved on this visit to its quote pages. Every visit starts with an empty cart;
 * options saved on an earlier visit stay on the submission and are quoted from its Coverage Options. The visit holds
 * across the quote pages (simple, customize, another product) and ends when the quote view is left. The cart modals
 * open outside the page, so the visit is kept here rather than in the page's state.
 */
const NONE: readonly string[] = [];
const visits = new Map<string, readonly string[]>();
// Options that were on the submission before this visit and were saved into in place (the page was
// opened with ?optionId, e.g. an excess tower's Request Quote). They belong to the Coverages tab.
const opened = new Map<string, readonly string[]>();
const listeners = new Set<() => void>();

const setVisit = (submissionId: string, ids: readonly string[]) => {
  visits.set(submissionId, ids);
  listeners.forEach((listener) => listener());
};

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

/** The ids of the options saved on this visit. */
export const useQuoteCartVisit = (
  submissionId: string | undefined
): readonly string[] => {
  const read = () => visits.get(submissionId ?? '') ?? NONE;
  return useSyncExternalStore(subscribe, read, read);
};

/**
 * Puts a saved option in this visit's cart. `wasOpened` marks an option that existed before the visit
 * and was saved into in place, so removing it from the cart must not delete it.
 */
export const addToQuoteCartVisit = (
  submissionId: string,
  optionId: string,
  wasOpened = false
) => {
  const openedIds = opened.get(submissionId) ?? NONE;
  if (wasOpened && !openedIds.includes(optionId)) {
    opened.set(submissionId, [...openedIds, optionId]);
  }
  const ids = visits.get(submissionId) ?? NONE;
  if (!ids.includes(optionId)) {
    setVisit(submissionId, [...ids, optionId]);
  }
};

/** True when the option is in this visit's cart. */
export const isInQuoteCartVisit = (
  submissionId: string,
  optionId: string
): boolean => (visits.get(submissionId) ?? NONE).includes(optionId);

/** True when the option was on the submission before this visit, so the cart only lets go of it. */
export const isOpenedInQuoteCartVisit = (
  submissionId: string,
  optionId: string
): boolean => (opened.get(submissionId) ?? NONE).includes(optionId);

/** Takes an option out of this visit's cart without touching it on the submission. */
export const dropFromQuoteCartVisit = (
  submissionId: string,
  optionId: string
) =>
  setVisit(
    submissionId,
    (visits.get(submissionId) ?? NONE).filter((id) => id !== optionId)
  );

/** Empties the cart: a new visit. */
export const startQuoteCartVisit = (submissionId: string) => {
  opened.set(submissionId, NONE);
  setVisit(submissionId, NONE);
};

/** Empties the cart each time a submission's quote view is entered, including a move to another submission's. */
export const useStartQuoteCartVisit = (
  submissionId: string,
  inQuoteView: boolean
) => {
  const visiting = useRef<string | null>(null);
  useEffect(() => {
    if (!inQuoteView) {
      visiting.current = null;
      return;
    }
    if (visiting.current !== submissionId) {
      startQuoteCartVisit(submissionId);
      visiting.current = submissionId;
    }
  }, [submissionId, inQuoteView]);
};

/** The cart: this product's unquoted options that were saved on this visit. */
export const cartOptionsFor = (
  product: string,
  options: CoverageOption[],
  visitIds: readonly string[]
): CoverageOption[] =>
  unquotedOptionsFor(product, options).filter((option) =>
    visitIds.includes(option.id)
  );
